import React, { useState, useEffect } from "react";
import axios from "axios";
import { Typeahead } from "react-bootstrap-typeahead";
import { navigate } from "@reach/router";
import { AuthConsumer } from "../helpers/AuthContext";
import {
  EDU_OPTIONS,
  YEAR_OPTIONS,
  CLEAN_OPTIONS,
} from "../constants/profileOptions";

// Profile-completion form for Google signups. Google gives us only name +
// email, so the expertise/affiliation fields (which power label weighting)
// are collected here before onboarding. Reuses the exact fields and option
// lists from the email/password SignUp form.
// Fuzzy match for the lab suggestion box: ignore case, spaces, hyphens, and
// punctuation so typing "ferris lab" surfaces an existing "Ferris-Lab" to
// pick. Suggestion-only — never auto-merges; the admin merge tool is the
// deliberate way to combine differently-named labs.
const normLab = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const labFilterBy = (option, props) => {
  const opt = typeof option === "string" ? option : (option && option.lab) || "";
  const typed = normLab(props.text);
  if (!typed) return true;
  return normLab(opt).includes(typed);
};

const CompleteProfile = () => {
  const [assoc, setAssoc] = useState([]);
  const [typedAssoc, setTypedAssoc] = useState("");
  const [lab, setLab] = useState([]);
  const [typedLab, setTypedLab] = useState("");
  const [labOptions, setLabOptions] = useState([]);
  const [prefilledLab, setPrefilledLab] = useState("");
  const [edu, setEdu] = useState("");
  const [exp, setExp] = useState("");
  const [expEEG, setExpEEG] = useState("");
  const [expMoBI, setExpMoBI] = useState("");
  const [expICA, setExpICA] = useState("");
  const [cleanFreq, setCleanFreq] = useState("");

  const [options, setOptions] = useState([]);
  const [prefilledAssoc, setPrefilledAssoc] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let info = {};
    try {
      info = JSON.parse(localStorage.getItem("Info") || "{}");
    } catch (e) {
      info = {};
    }
    const headers = info.accessToken
      ? { "x-access-token": info.accessToken }
      : {};
    const email = localStorage.getItem("Email");

    // Association typeahead options (non-fatal — falls back to free text).
    axios
      .get("/api/users/userlist", { headers })
      .then((res) => {
        const unique = [
          ...new Set(res.data.map((u) => u.assoc).filter((a) => a !== undefined)),
        ];
        setOptions(unique);
        // Existing lab names, so the picker suggests them (pick over retype).
        const labs = [
          ...new Set(res.data.map((u) => (u.lab || "").trim()).filter(Boolean)),
        ].sort((a, b) => a.localeCompare(b));
        setLabOptions(labs);
      })
      .catch(() => {});

    // Pre-fill whatever this account already has so legacy users only add
    // the missing fields (and don't overwrite their existing institution /
    // degree / experience). Google signups have nothing to pre-fill.
    if (email && email !== "guest") {
      axios
        .get("/api/users/user", { params: { email }, headers })
        .then((res) => {
          const u = res.data || {};
          if (u.assoc) {
            setTypedAssoc(u.assoc);
            setPrefilledAssoc(u.assoc);
          }
          if (u.lab) setPrefilledLab(u.lab);
          if (u.edu) setEdu(u.edu);
          if (u.exp) setExp(u.exp);
          if (u.expEEG) setExpEEG(u.expEEG);
          if (u.expMoBI) setExpMoBI(u.expMoBI);
          if (u.expICA) setExpICA(u.expICA);
          if (u.cleanFreq) setCleanFreq(u.cleanFreq);
        })
        .catch(() => {})
        .finally(() => setLoaded(true));
    } else {
      setLoaded(true);
    }
  }, []);

  const resolveAssoc = () => {
    if (typeof assoc[0] === "string" || assoc[0] instanceof String) return assoc[0];
    if (typeof assoc[0] === "object" && assoc[0] !== null) return assoc[0].assoc;
    return typedAssoc;
  };

  // Lab is optional. Prefer a picked/typed value; fall back to the prefilled
  // value (what the user already had) so a no-op save doesn't blank it.
  const resolveLab = () => {
    if (typeof lab[0] === "string" || lab[0] instanceof String) return lab[0];
    if (typeof lab[0] === "object" && lab[0] !== null) return lab[0].lab || "";
    if (typedLab) return typedLab;
    return prefilledLab;
  };

  const validate = (assocSel) => {
    if (!assocSel) return "Select or enter your university / company.";
    if (!edu) return "Select your highest degree.";
    if (!exp || !expEEG || !expMoBI || !expICA) return "Answer all expertise questions.";
    if (!cleanFreq) return "Tell us how often you clean data.";
    return null;
  };

  const handleSubmit = (completeProfile) => {
    const assocSel = resolveAssoc();
    const err = validate(assocSel);
    if (err) {
      setMessage(err);
      return;
    }
    setIsSubmitting(true);
    setMessage("");
    completeProfile({
      assoc: assocSel,
      lab: resolveLab().trim(),
      edu,
      exp,
      expEEG,
      expMoBI,
      expICA,
      cleanFreq,
    })
      .then(() => {
        setIsSubmitting(false);
        // Established users (already onboarded / have practiced / labeled)
        // go STRAIGHT back to the app — they don't need the new-user
        // "welcome + do the practice round" flow. Only brand-new accounts
        // (a fresh Google signup) get onboarding.
        let established = false;
        try {
          const info = JSON.parse(localStorage.getItem("Info") || "{}");
          established = !!(
            info.tutorialCompleted ||
            info.practicePassed ||
            (info.practiceCount || 0) > 0
          );
        } catch (e2) {
          // fall back to onboarding if we can't tell
        }
        navigate(established ? "/dashboard" : "/onboarding");
      })
      .catch((e) => {
        setIsSubmitting(false);
        setMessage(
          e?.response?.data?.message || "Couldn't save your profile. Try again."
        );
      });
  };

  return (
    <AuthConsumer>
      {({ Auth, Name, completeProfile }) => {
        const isLoggedIn = Auth === "Admin" || Auth === "User";

        // This page only makes sense for a logged-in account. If somehow
        // reached while signed out, send them to sign in.
        if (!isLoggedIn) {
          return (
            <div className="theme-scientific auth-page">
              <div className="auth-shell auth-shell--solo-wide">
                <div className="auth-form-panel">
                  <div className="sci-eyebrow">ICMOBI / Sign in required</div>
                  <h2 className="auth-form-panel__title">Please sign in first</h2>
                  <button
                    className="sci-btn sci-btn--primary sci-btn--full"
                    style={{ marginTop: 20 }}
                    onClick={() => navigate("/login")}
                  >
                    Go to sign in →
                  </button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="theme-scientific auth-page">
            <div className="auth-shell auth-shell--solo-wide">
              <section className="auth-form-panel">
                <header className="auth-form-panel__header">
                  <div className="sci-eyebrow">ICMOBI / Complete your profile</div>
                  <h2 className="auth-form-panel__title">
                    Almost there{Name && Name !== "Guest" ? `, ${Name}` : ""}
                  </h2>
                  <p className="auth-form-panel__subtitle">
                    A few details about your background help us weight your labels.
                  </p>
                </header>

                {message && (
                  <div className="sci-alert sci-alert--danger">
                    <div className="sci-alert__icon" />
                    <span>{message}</span>
                  </div>
                )}

                {!loaded ? (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div className="sci-alert sci-alert--success">
                      <div className="sci-alert__icon" />
                      <span>Loading your profile…</span>
                    </div>
                  </div>
                ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(completeProfile);
                  }}
                >
                  <div
                    className="sci-section"
                    style={{ marginTop: 0, paddingTop: 0, borderTop: 0 }}
                  >
                    <div className="sci-section__title">Affiliation</div>
                    <div className="sci-field">
                      <label className="sci-field__label sci-field__label--req">
                        University / company
                      </label>
                      <Typeahead
                        id="cp-assoc-typeahead"
                        labelKey="assoc"
                        allowNew
                        defaultInputValue={prefilledAssoc}
                        onChange={setAssoc}
                        onInputChange={setTypedAssoc}
                        options={options}
                        placeholder="Start typing an institution…"
                      />
                    </div>
                    <div className="sci-field">
                      <label className="sci-field__label">
                        Research lab / group <span style={{ fontWeight: 400, color: "#6b7280" }}>(optional)</span>
                      </label>
                      <Typeahead
                        id="cp-lab-typeahead"
                        labelKey="lab"
                        allowNew
                        newSelectionPrefix="Add new lab: "
                        filterBy={labFilterBy}
                        defaultInputValue={prefilledLab}
                        onChange={setLab}
                        onInputChange={setTypedLab}
                        options={labOptions}
                        placeholder="Pick your lab, or type to add a new one…"
                      />
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                        Pick your lab from the list if it's already there — it
                        keeps your lab's competition score together.
                      </div>
                    </div>
                  </div>

                  <div className="sci-section">
                    <div className="sci-section__title">Expertise ratings</div>
                    <div className="sci-field-row sci-field-row--2">
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Highest degree
                        </label>
                        <select
                          className="sci-select"
                          value={edu}
                          onChange={(e) => setEdu(e.target.value)}
                        >
                          {EDU_OPTIONS.map((o) => (
                            <option key={o.value || "placeholder"} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Years labeling EEG
                        </label>
                        <select
                          className="sci-select"
                          value={exp}
                          onChange={(e) => setExp(e.target.value)}
                        >
                          {YEAR_OPTIONS.map((o) => (
                            <option key={"labeling-" + (o.value || "p")} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sci-field-row sci-field-row--3">
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Years with EEG
                        </label>
                        <select
                          className="sci-select"
                          value={expEEG}
                          onChange={(e) => setExpEEG(e.target.value)}
                        >
                          {YEAR_OPTIONS.map((o) => (
                            <option key={"eeg-" + (o.value || "p")} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Years with MoBI
                        </label>
                        <select
                          className="sci-select"
                          value={expMoBI}
                          onChange={(e) => setExpMoBI(e.target.value)}
                        >
                          {YEAR_OPTIONS.map((o) => (
                            <option key={"mobi-" + (o.value || "p")} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sci-field">
                        <label className="sci-field__label sci-field__label--req">
                          Years with ICA
                        </label>
                        <select
                          className="sci-select"
                          value={expICA}
                          onChange={(e) => setExpICA(e.target.value)}
                        >
                          {YEAR_OPTIONS.map((o) => (
                            <option key={"ica-" + (o.value || "p")} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sci-field">
                      <label className="sci-field__label sci-field__label--req">
                        How often do you clean data yourself?
                      </label>
                      <select
                        className="sci-select"
                        value={cleanFreq}
                        onChange={(e) => setCleanFreq(e.target.value)}
                      >
                        {CLEAN_OPTIONS.map((o) => (
                          <option key={"clean-" + (o.value || "p")} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="sci-btn sci-btn--primary sci-btn--full"
                    disabled={isSubmitting}
                    style={{ marginTop: 24 }}
                  >
                    {isSubmitting ? "Saving…" : "Save and continue →"}
                  </button>
                </form>
                )}
              </section>
            </div>
          </div>
        );
      }}
    </AuthConsumer>
  );
};

export default CompleteProfile;
